import { useTranslation } from 'react-i18next';
import { useEffect, useContext, useState, useRef } from 'react';
import { NavContext } from '@/components/NavContext';
import { ArrowLeft, Cross } from '@teo/components/icons';
import { PageLayout } from '@/components/PageLayout';
import { AuthContext } from '@/components/Contexts/AuthContext';
import { Button, IconButton, TEOLoader } from '@teo/components';
import { useGet } from '@/query/fetchHooks';
import { useParams, useNavigate } from 'react-router-dom';
import { LessonView } from '@/components/LessonView';
import { useMediaQuery } from 'react-responsive';
import LessonHeader from '../../Learn/Courses/Lessons/LessonHeader';
import { PreviewTree } from './PreviewTree';
import { Portal } from '@/components/Portal';
import i18n from 'i18next';
import { parseQueryString, newUid } from '@/util';
import { ExtraInfoLesson } from '../../Learn/Courses/Lessons/ExtraInfoLesson';
import {
  useContentLinks,
  matchesContentLink,
} from '@/components/useContentLinks';
import { ImageZoom } from '@/components/ImageZoom';
import { clsx } from 'clsx';
import { config } from '@/config';
import { axios } from '@/axios';

function PreviewCourse({
  showAnswers = false,
  instruction,
  isPreview = false,
}: {
  showAnswers?: boolean;
  instruction?: boolean;
  isPreview?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setNavDisabled } = useContext(NavContext);
  const { coursecontentId, courseId, lessonId } = useParams();
  const { auth, setAuth } = useContext(AuthContext);
  const userId = auth?.user?.id;
  const authUser = auth?.user;
  const [activeNode, setActiveNode] = useState<any>(null);
  const [hideContent, setHideContent] = useState<boolean>(false);
  const [nrOfPages, setNrOfPages] = useState(1);
  const [page, setPage] = useState(0);
  const [hasWidgets, setHasWidgets] = useState<boolean>(false);
  const [viewMobile, setViewMobile] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);

  const [allExtraInfo, setAllExtraInfo] = useState(null);
  const [allLessons, setAllLessons] = useState<any[]>([]);
  const [rootCourseId, setRootCourseId] = useState(null);
  const [allDirectories, setAllDirectories] = useState(null);

  const [showExtraInfo, setShowExtraInfo] = useState(false);
  const [extraInfoFullScreen, setExtraInfoFullScreen] = useState(false);
  const [activeExtraInfo, setActiveExtraInfo] = useState(null);
  const [lengthExtraInfo, setLengthExtraInfo] = useState(0);
  const [imageZoom, setImageZoom] = useState(false);
  const [nextLesson, setNextLesson] = useState<any>(null);
  const [previousLesson, setPreviousLesson] = useState<any>(null);

  const [isLoaded, setIsLoaded] = useState(false);

  const [course, setCourse] = useState<any>(null);
  const [courseContent, setCourseContent] = useState<any>(null);

  const [refTree, setRefTree] = useState(newUid(10));

  const queries = parseQueryString(document.location.search) as {
    [key: string]: string;
  };

  const role = Math.max(
    authUser?.role || 0,
    authUser?.activeOrganisationRole || 0
  );
  if (role <= 20) showAnswers = false;

  let basePath: any;
  if (courseId) {
    basePath = window.location.pathname.slice(
      0,
      window.location.pathname.lastIndexOf('/less')
    );
  } else {
    basePath = window.location.pathname.slice(
      0,
      window.location.pathname.lastIndexOf('/p')
    );
  }

  if (queries['returnUrl']) {
    basePath = decodeURIComponent(queries['returnUrl']);
  }

  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  const iSsmallLaptop = useMediaQuery({
    query: '(max-width: 1199px)',
  });

  useEffect(() => {
    if (instruction && activeNode) {
      if (activeNode?.lessonId || activeNode?.assetId) {
        axios.put(`${config.backend}/follow/${courseId}/${userId}`, {
          courseId: courseId,
          currentLessonId: activeNode.lessonId || activeNode?.assetId,
          userId: userId,
        });
      }
    }
  }, [activeNode]);

  useEffect(() => {
    if (!activeNode) return;

    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [activeNode]);

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const viewVersion: any = queryParams.get('view');
    if (viewVersion == 'mobile') {
      setViewMobile(true);
      setNavDisabled(true);
      return () => setNavDisabled(false);
    }
  }, []);

  useEffect(() => {
    setNavDisabled(true);
    return () => setNavDisabled(false);
  }, []);

  useEffect(() => {
    if (showExtraInfo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
  }, [showExtraInfo]);

  useEffect(() => {
    hideContent && !viewMobile && setActiveNode(null);
    setRefTree(newUid(10));
  }, [hideContent]);

  useContentLinks({
    onLesson: (target) => {
      const node = allLessons.find((x) => matchesContentLink(x, target));
      if (!node) return;
      setShowExtraInfo(false);
      setActiveExtraInfo(null);
      setIsLoaded(true);
      setActiveNode(node);
    },
    onExtraInfo: (target) => {
      const matches = ((allExtraInfo as any) || []).filter((x: any) =>
        matchesContentLink(x, target)
      );
      const item =
        matches.find((x: any) => x?.rootLessonId == activeNode?.lessonId) ||
        matches[0];
      if (!item) return;
      setActiveExtraInfo(item);
      setShowExtraInfo(true);
    },
  });

  useGet(`/coursedetail/${courseId}`, {
    enabled: !!courseId,
    onSuccess: (result: any) => {
      setCourse(result?.data || {});
    },
  });

  useGet(`/coursecontent/${coursecontentId}`, {
    enabled: !!coursecontentId,
    onSuccess: (result: any) => {
      setCourseContent(result?.data || {});
    },
  });

  return (
    <>
      {viewMobile && !isMobile && (
        <IconButton
          className="absolute left-5 top-5"
          Icon={ArrowLeft}
          variant="outline"
          onClick={() => {
            navigate(
              `${basePath}?les=${activeNode?.usesId ? activeNode?.usesId : 0}`
            );
          }}
        />
      )}
      <div
        className={`flex min-h-screen flex-col ${
          viewMobile &&
          'mx-auto !my-5 w-full max-w-sm overflow-hidden rounded-lg border border-grey-02'
        }`}
      >
        <Portal root="head">
          <meta name="export-placeholder" content="" />
        </Portal>
        {!(hideContent && (isMobile || viewMobile)) &&
          !(showExtraInfo && (isMobile || viewMobile)) && (
            <LessonHeader
              authUser={authUser}
              setAuth={setAuth}
              userId={userId}
              course={course || courseContent}
              coursePath={
                showAnswers
                  ? queries['returnUrl']
                    ? basePath
                    : `/${i18n.language}/follow-up/${authUser?.activeOrganisationId}/courses/${coursecontentId}`
                  : basePath
              }
              setHideContent={setHideContent}
              hideContent={hideContent}
              activeLesson={activeNode}
              couresPreview={
                course?.courseType === 'instruction' ? false : true
              }
              activeNode={activeNode}
              setActiveNode={setActiveNode}
              viewMobile={viewMobile}
              setShowExtraInfo={setShowExtraInfo}
              setExtraInfoFullScreen={setExtraInfoFullScreen}
              extraInfoFullScreen={extraInfoFullScreen}
              setActiveExtraInfo={setActiveExtraInfo}
              lengthExtraInfo={lengthExtraInfo}
              iSsmallLaptop={iSsmallLaptop}
              isPreview={isPreview}
              showAnswers={showAnswers}
            />
          )}
        <PageLayout>
          {/* {(!isMobile || (!activeNode && isMobile)) && ( */}
          <PageLayout.Sidebar
            className={`md:!max-w-[380px]] relative w-full border-r border-solid border-grey-02 !p-0  ${
              (isMobile || viewMobile) && !hideContent ? '!hidden' : null
            }`}
          >
            <div
              className={`sticky max-h-[calc(100vh_-_80px)] overflow-y-auto p-5 md:w-[380px] ${
                !(hideContent && (isMobile || viewMobile)) && 'top-[80px]'
              }`}
            >
              <div
                className={`flex w-full items-center ${
                  (isMobile || viewMobile) && '-ml-3 -mr-3'
                }`}
              >
                <p
                  className={`mt-2 mb-4 pl-2 text-base font-medium ${
                    (isMobile || viewMobile) && 'mx-auto mb-6'
                  }`}
                >
                  {t('pages.course_landing.contents')}
                </p>
                {hideContent && (isMobile || viewMobile) && (
                  <IconButton
                    Icon={Cross}
                    variant="ghost"
                    className="mb-4 p-2.5"
                    onClick={() => {
                      setHideContent(false);
                    }}
                  />
                )}
              </div>
              <PreviewTree
                key={refTree}
                courseId={courseId}
                coursecontentId={
                  courseId
                    ? (course?.coursecontentId as string)
                    : (coursecontentId as string)
                }
                lessonId={lessonId}
                activeNode={activeNode}
                setActiveNode={setActiveNode}
                setHideContent={setHideContent}
                setAllExtraInfo={setAllExtraInfo}
                setAllLessons={setAllLessons}
                setRootCourseId={setRootCourseId}
                setAllDirectories={setAllDirectories}
                setNextLesson={setNextLesson}
                setPreviousLesson={setPreviousLesson}
                setIsLoaded={setIsLoaded}
              />
            </div>
          </PageLayout.Sidebar>
          {/* )}  */}
          <div
            className={`relative flex grow p-4 ${
              (isMobile || viewMobile) && hideContent ? '!hidden' : ''
            } ${activeNode?.onlyTheory ? 'onlyTheory' : ''} ${
              hasWidgets ? 'noWidgets' : ''
            } ${viewMobile && 'relative'}`}
          >
            {isLoaded && (
              <div className="absolute inset-0 z-[11] flex bg-white">
                <div className="m-auto">
                  <TEOLoader
                    loaderSrc="/images/TEOloader.gif"
                    className="w-[110px]"
                  />
                </div>
              </div>
            )}
            <div className="fr-view mx-auto w-full max-w-2xl">
              {activeNode && (
                <>
                  {activeNode?.lessonId && (
                    <LessonView
                      showAnswers={showAnswers}
                      pageBreak={!queries['noPageBreak']}
                      node={activeNode}
                      course={course || courseContent}
                      page={page}
                      setSubmitFn={() => {
                        const widgets =
                          document?.querySelectorAll('.widget_practical');
                        if (widgets?.length > 0) {
                          setHasWidgets(true);
                        } else {
                          setHasWidgets(false);
                        }
                      }}
                      setNrOfPages={(nrOfPages: number) => {
                        setPage(0);
                        setNrOfPages(nrOfPages);
                      }}
                      setIsLoaded={setIsLoaded}
                    ></LessonView>
                  )}
                  {activeNode?.assetId && activeNode?.assetPath && (
                    <div className="flex w-full items-start p-3">
                      {activeNode?.assetType == 'image' ? (
                        <>
                          <ImageZoom
                            activeExtraInfo={activeNode}
                            extraInfoFullScreen={true}
                            setImageZoom={setImageZoom}
                            imageZoom={imageZoom}
                            onLoad={() => {
                              setLoaded(true);
                              setIsLoaded(false);
                            }}
                          />
                          <div className={clsx(loaded && 'page-loaded')}></div>
                        </>
                      ) : (
                        <>
                          {activeNode?.assetType == 'video' ? (
                            <div id={`${newUid(20)}_embed`} className="mx-auto">
                              <iframe
                                id={`${newUid(20)}_placeholder`}
                                width="640"
                                height="360"
                                className=""
                                src={activeNode?.assetPath}
                                onLoad={() => {
                                  setLoaded(true);
                                  setIsLoaded(false);
                                }}
                              ></iframe>
                              <div
                                className={clsx(loaded && 'page-loaded')}
                              ></div>
                            </div>
                          ) : (
                            <div
                              id={`${newUid(20)}_embed`}
                              className="mx-auto"
                              dangerouslySetInnerHTML={{
                                __html: activeNode?.assetPath,
                              }}
                            />
                          )}
                        </>
                      )}
                    </div>
                  )}
                </>
              )}
              {/* {!activeNode?.assetId && ( */}
              <div className="teo_submit_row mt-6 flex justify-between">
                {(page == 0 || activeNode?.assetId) && previousLesson && (
                  <Button
                    className="mr-auto"
                    name="previous_lesson"
                    onClick={() => {
                      setIsLoaded(true);
                      setActiveNode(previousLesson);
                    }}
                    variant="secondary"
                  >
                    {t('pages.course_landing.previous')}
                  </Button>
                )}
                {page > 0 && !activeNode?.assetId && (
                  <Button
                    className="mr-auto"
                    name="previous_page"
                    onClick={async () => {
                      setPage(page - 1);
                      window.scrollTo(0, 0);
                    }}
                  >
                    {t('pages.course_landing.previous_page')}
                  </Button>
                )}
                {page < nrOfPages - 1 && !activeNode?.assetId && (
                  <Button
                    name="next_page"
                    className="ml-auto"
                    onClick={async () => {
                      setPage(page + 1);
                      window.scrollTo(0, 0);
                    }}
                  >
                    {t('pages.course_landing.next_page')}
                  </Button>
                )}
                {((!hasWidgets && page == nrOfPages - 1) ||
                  activeNode?.assetId) &&
                  nextLesson && (
                    <Button
                      name="next_lesson"
                      onClick={() => {
                        setIsLoaded(true);
                        setActiveNode(nextLesson);
                      }}
                      className="ml-auto"
                    >
                      {t('pages.course_landing.next')}
                    </Button>
                  )}
                {hasWidgets &&
                  !showAnswers &&
                  !activeNode?.assetId &&
                  page === nrOfPages - 1 && (
                    <Button
                      className="ml-auto"
                      name="submit"
                      onClick={() => {
                        setIsLoaded(true);
                        setActiveNode(nextLesson);
                      }}
                    >
                      {t('pages.course_landing.submit')}
                    </Button>
                  )}
              </div>
              {/* )} */}
            </div>
            {!activeNode?.assetId &&
              activeNode?.lessonType !== 'instruction' && (
                <div className="">
                  <ExtraInfoLesson
                    node={activeNode}
                    course={null}
                    rootCourseId={rootCourseId}
                    allExtraInfo={allExtraInfo}
                    allDirectories={allDirectories}
                    setShowExtraInfo={setShowExtraInfo}
                    showExtraInfo={showExtraInfo}
                    setExtraInfoFullScreen={setExtraInfoFullScreen}
                    extraInfoFullScreen={extraInfoFullScreen}
                    setActiveExtraInfo={setActiveExtraInfo}
                    activeExtraInfo={activeExtraInfo}
                    setLengthExtraInfo={setLengthExtraInfo}
                    lengthExtraInfo={lengthExtraInfo}
                    preview={true}
                    iSsmallLaptop={iSsmallLaptop}
                    isMobile={isMobile}
                    viewMobile={viewMobile}
                  />
                </div>
              )}
          </div>
        </PageLayout>
      </div>
    </>
  );
}

export default PreviewCourse;
